import {
  trapFocus,
  removeTrapFocus,
  setInertOnSiblings,
  removeInertFromSiblings,
  setBackgroundAriaHidden,
} from '@/lib/a11y'

class Drawer extends window.HTMLElement {
  parent = this.parentNode
  activeElement = null
  escapeHandler = null
  boundOpen = null

  constructor() {
    super()
    this.boundOpen = this.open.bind(this)
    this.escapeHandler = this.handleEscape.bind(this)

    const triggers = document.querySelectorAll(`[aria-controls="${this.id}"]`)

    if (triggers && triggers.length > 0) {
      triggers.forEach((trigger) => {
        trigger.addEventListener('click', this.boundOpen)
      })
    }
  }

  /**
   * Handles the Escape key press to close the drawer.
   * @param {KeyboardEvent} evt - The keyboard event.
   */
  handleEscape(evt) {
    if (evt.code === 'Escape' && this.hasAttribute('open')) {
      this.close()
    }
  }

  disconnectedCallback() {
    const triggers = document.querySelectorAll(`[aria-controls="${this.id}"]`)

    // check if triggers exist before removing event listeners
    if (triggers && triggers.length > 0) {
      triggers.forEach((trigger) => {
        trigger.removeEventListener('click', this.boundOpen)
      })
    }

    // Clean up escape listener
    document.removeEventListener('keyup', this.escapeHandler)
  }

  /**
   * Opens the drawer and manages focus and accessibility.
   * @param {HTMLElement} triggeredBy - The element that triggered the opening (optional).
   */
  open(triggeredBy) {
    // Store the trigger element for focus return on close
    if (triggeredBy instanceof HTMLElement) {
      this.setActiveElement(triggeredBy)
    } else if (
      document.activeElement &&
      document.activeElement !== document.body
    ) {
      this.setActiveElement(document.activeElement)
    }

    this.dispatchEvent(new window.CustomEvent('drawer:open'))

    if (this.shouldLock) {
      document.body.classList.add('overflow-hidden')
    }

    // Set inert on background content to prevent focus escape
    setInertOnSiblings(this)

    // Set aria-hidden on main content
    setBackgroundAriaHidden(true, this)

    const shadowRootContent = this.shadowRoot.querySelector('.drawer-content')

    // Set open attribute to make drawer visible (display: block)
    this.setAttribute('open', true)

    // Get the form element and force initial state to be computed for slide animation
    const formEl = this.querySelector('facet-filters-form')
    if (formEl) {
      // Force browser to compute initial translateX(100%) state
      // eslint-disable-next-line no-unused-expressions
      formEl.offsetHeight
    }

    // Now trigger the animation in next frame
    requestAnimationFrame(() => {
      shadowRootContent.style.opacity = 1
      this.classList.add('open')

      // Animate slide by setting transform to 0
      if (formEl) {
        formEl.style.transform = 'translateX(0)'
      }

      // Move to body and trap focus after animation completes
      if (this.shouldAppendToBody) {
        setTimeout(() => {
          document.body.appendChild(this)
          if (this.shouldTrapFocus) {
            const focusTarget =
              this.querySelector('[autofocus]') ||
              this.querySelector(
                'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
              ) ||
              this
            trapFocus(this, focusTarget)
          }
        }, 500) // Wait for 500ms transition to complete
      } else if (this.shouldTrapFocus) {
        const focusTarget =
          this.querySelector('[autofocus]') ||
          this.querySelector(
            'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
          ) ||
          this
        trapFocus(this, focusTarget)
      }
    })
  }

  /**
   * Sets the active element for focus management.
   * @param {HTMLElement} element - The element to set as active.
   */
  setActiveElement(element) {
    this.activeElement = element
  }

  close() {
    const shadowRootContent = this.shadowRoot.querySelector('.drawer-content')

    // Reset inline transform so CSS translate-x-full takes effect for next open
    const formEl = this.querySelector('facet-filters-form')
    if (formEl) {
      formEl.style.transform = ''
    }

    requestAnimationFrame(() => {
      shadowRootContent.style.opacity = 0
      this.classList.remove('open')
    })
    this.dispatchEvent(new window.CustomEvent('drawer:close'))

    // Remove inert from background content
    removeInertFromSiblings()

    if (this.shouldLock) {
      document.body.classList.remove('overflow-hidden')
    }

    if (this.shouldAppendToBody) {
      // if the parent includes an element of the same id, kill it off. it's a zombie.

      const parentElement = this.parent.querySelector(`#${this.id}`)

      if (parentElement) {
        parentElement.remove()
      }

      // move the drawer back to its original position
      this.parent.appendChild(this)
    }

    this.removeAttribute('open')

    // Remove aria-hidden from background content
    setBackgroundAriaHidden(false, this)

    // Remove focus trap and return focus to trigger element
    removeTrapFocus(this.activeElement)

    document.dispatchEvent(new CustomEvent('theme:loading:cancel'), {
      bubbles: true,
    })
  }

  get shouldLock() {
    return true
  }

  get shouldTrapFocus() {
    return true
  }

  get shouldAppendToBody() {
    return true
  }

  connectedCallback() {
    // assume there are multiple triggers for the same drawer

    this.setAttribute('role', 'dialog')
    this.setAttribute('aria-modal', 'true')
    if (!this.shadowRoot) {
      this.attachShadow({ mode: 'open' })
      this.render()
    }

    // Register escape handler when connected (re-added after appendChild moves element)
    document.addEventListener('keyup', this.escapeHandler)

    // Re-register trigger click handlers (re-added after appendChild moves element)
    const triggers = document.querySelectorAll(`[aria-controls="${this.id}"]`)
    if (triggers && triggers.length > 0) {
      triggers.forEach((trigger) => {
        // Remove first to avoid duplicate listeners
        trigger.removeEventListener('click', this.boundOpen)
        trigger.addEventListener('click', this.boundOpen)
      })
    }
  }

  render() {
    this.shadowRoot.innerHTML = `
        <style>
          :host {
            display: none;
            position: fixed;
            z-index: 9999999;
            left: 0;
            top: 0;
            width: 100%;
            height: 100%;
            justify-content: center;
            align-items: center;
            overflow: auto;
            opacity: 0; /* Initial opacity set to 0 */
            visibility: hidden; /* Initial visibility set to hidden */
            backdrop-filter: blur(0px);
            -webkit-backdrop-filter: blur(0px);
            transition: opacity 300ms ease-in-out, backdrop-filter 300ms ease-in-out, -webkit-backdrop-filter 300ms ease-in-out;
            pointer-events: none;
            overflow: hidden;
          }

          .drawer-content {
            display: flex;
            justify-content: start;
            align-items: center;
            height: 100%;
            width: 100%;
            opacity: 0;
            transition: opacity 300ms ease-in-out;
          }

          .drawer-background {
            background-color: rgba(0, 0, 0, 0.4);
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            z-index: -1;
          }

          :host([open]) {
            display: block !important;
            opacity: 1;
            visibility: visible;
            pointer-events: auto;
            backdrop-filter: blur(4px);
            -webkit-backdrop-filter: blur(4px);
          }

          @media (prefers-reduced-motion: reduce) {
            :host {
              transition-duration: 0ms;
            }
            .drawer-content {
              transition-duration: 0ms;
            }
          }

          :host([slide-mobile="top"]) .drawer-content{
            justify-content: center;
            align-items: flex-start;
          }

          :host([slide-mobile="bottom"]) .drawer-content{
            justify-content: center;
            align-items: flex-end;
          }

          :host([slide-desktop="top"]) .drawer-content{
            justify-content: center;
            align-items: flex-start;
          }

          :host([slide-mobile="right"]) .drawer-content{
            justify-content: flex-end;
          }

          :host([slide-mobile="right"]) .drawer-content{
            justify-content: flex-end;
          }

          :host([slide-desktop="right"]) .drawer-content{
            justify-content: flex-end;
          }
          :host([slide-desktop="left"]) .drawer-content{
            justify-content: flex-start;
          }


        </style>
        <div class="drawer-content">
          <div class="drawer-background" aria-hidden="true"></div>
          <slot></slot>
        </div>
      `

    const shadowRootContent = this.shadowRoot.querySelector('.drawer-content')

    shadowRootContent.addEventListener('click', (evt) => {
      if (evt.target === shadowRootContent) {
        this.close()
      }
    })
  }
}

window.Drawer = Drawer
